import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, ShoppingBag, Loader2, Tag, Wallet, Store, Truck, Banknote, ShieldCheck, Upload, Clock, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useCurrency } from "@/context/CurrencyContext";

export default function Cart() {
  const { cart, updateCart, removeCart } = useCart();
  const { user } = useAuth();
  const { t } = useI18n();
  const { format, currency, rates } = useCurrency();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [payMethod, setPayMethod] = useState("stripe");
  const [credit, setCredit] = useState(0);
  const [creditExpiry, setCreditExpiry] = useState(null);
  const [useCredit, setUseCredit] = useState(false);
  const [age, setAge] = useState(null);
  const [uploadingId, setUploadingId] = useState(false);
  const [ngZones, setNgZones] = useState([]);
  const [ngState, setNgState] = useState(() => (typeof window !== "undefined" ? localStorage.getItem("xurray_ng_state") || "" : ""));

  useEffect(() => {
    api.get("/shipping/nigeria-zones").then((r) => setNgZones(r.data.zones || [])).catch(() => setNgZones([]));
  }, []);

  const ngZone = ngState ? (ngZones.find((z) => z.states.includes(ngState)) || null) : null;
  const setNg = (state) => {
    setNgState(state);
    if (typeof window !== "undefined") { if (state) localStorage.setItem("xurray_ng_state", state); else localStorage.removeItem("xurray_ng_state"); }
  };

  const hasAlcohol = cart.some((i) => i.product?.category === "wine-alcohol");
  const ageOk = !hasAlcohol || age?.status === "approved";

  useEffect(() => {
    if (user) api.get("/buyer/wallet").then((r) => { setCredit(r.data?.credit_balance || 0); setCreditExpiry(r.data?.credit_expires_at || null); }).catch(() => setCredit(0));
  }, [user]);

  useEffect(() => {
    if (user && hasAlcohol) api.get("/buyer/age-verification").then((r) => setAge(r.data)).catch(() => setAge({ status: "none" }));
  }, [user, hasAlcohol]);

  const uploadId = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingId(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/buyer/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.post("/buyer/age-verification", { doc_url: up.data.url });
      setAge(data);
      toast.success("ID submitted — awaiting approval");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploadingId(false); }
  };

  const subtotal = cart.reduce((s, i) => s + (i.product?.discount_price || i.product?.price || 0) * i.quantity, 0);
  const fallbackBySeller = {};
  let shipping = 0;
  cart.forEach((i) => {
    const pf = i.product?.shipping_fee;
    if (pf === null || pf === undefined) {
      const sid = i.product?.seller_id;
      if (sid) fallbackBySeller[sid] = Number(i.product?.seller_shipping_fee || 0);
    } else {
      shipping += Number(pf) * i.quantity;
    }
  });
  shipping += Object.values(fallbackBySeller).reduce((s, f) => s + f, 0);
  if (ngZone) shipping = Number(ngZone.fee);  // deliveries within Nigeria use the flat state-zone fee
  const preCredit = subtotal - discount + shipping;
  const creditApplied = useCredit ? Math.min(credit, preCredit) : 0;
  const total = Math.max(preCredit - creditApplied, 0);

  const groups = Object.values(
    cart.reduce((acc, i) => {
      const sid = i.product?.seller_id || "unknown";
      if (!acc[sid]) acc[sid] = { seller_id: sid, name: i.product?.seller_name || "Store", items: [], subtotal: 0, handling_days: i.product?.handling_days, shipping_days: i.product?.shipping_days };
      acc[sid].items.push(i);
      acc[sid].subtotal += (i.product?.discount_price || i.product?.price || 0) * i.quantity;
      return acc;
    }, {})
  );

  const deliveryEta = (g) => {
    const opts = { weekday: "short", month: "short", day: "numeric" };
    const custom = g.handling_days != null || g.shipping_days != null;
    let lo, hi;
    if (custom) {
      const handling = Number(g.handling_days ?? 1);
      const transit = Number(g.shipping_days ?? 4);
      lo = handling + transit;
      hi = handling + transit + 2; // small buffer window
    } else {
      const seed = String(g.seller_id).split("").reduce((s, c) => s + c.charCodeAt(0), 0);
      const offset = seed % 3;
      lo = 3 + offset;
      hi = 6 + offset;
    }
    const from = new Date(Date.now() + lo * 86400000);
    const to = new Date(Date.now() + hi * 86400000);
    return `${from.toLocaleDateString(undefined, opts)} – ${to.toLocaleDateString(undefined, opts)}`;
  };

  const applyCoupon = async (codeArg) => {
    const code = ((typeof codeArg === "string" ? codeArg : "") || coupon || "").trim();
    if (!code) return;
    try {
      const { data } = await api.get(`/coupons/${code}`);
      setCoupon(code);
      setDiscount(subtotal * data.percent_off / 100);
      toast.success(`Coupon applied: ${data.percent_off}% off`);
    } catch { toast.error("Invalid coupon"); }
  };

  // Coupon auto-apply: a code tapped from the buyer Coupons gallery lands here.
  useEffect(() => {
    const pending = localStorage.getItem("xm_coupon");
    if (pending && cart.length > 0) {
      localStorage.removeItem("xm_coupon");
      applyCoupon(pending);
    }
  }, [cart.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkout = async (method) => {
    if (!user) return navigate("/login");
    if (!ageOk) { toast.error("Please verify your age to buy alcohol products"); return; }
    const pay = method || payMethod;
    // Paystack charges in NGN; Stripe handles EUR/USD (never NGN).
    const payCurrency = pay === "paystack" ? "NGN" : (currency === "NGN" ? "EUR" : currency);
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", { origin_url: window.location.origin, coupon_code: coupon || null, currency: payCurrency, use_store_credit: useCredit });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  const ngnTotal = Math.round(total * (rates?.NGN || 1750));

  if (cart.length === 0) return (
    <Layout>
      <div className="max-w-2xl mx-auto text-center py-24">
        <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
        <h1 className="font-display font-bold text-2xl">{t("emptyCart")}</h1>
        <Link to="/products"><Button className="mt-4 rounded-full" data-testid="shop-now-empty">{t("startShopping")}</Button></Link>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-bold text-3xl mb-6">{t("shoppingCart")}</h1>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-5">
            {groups.map((g) => (
              <div key={g.seller_id} data-testid={`cart-seller-group-${g.seller_id}`} className="rounded-2xl border border-border/60 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-muted/40 border-b border-border/60">
                  <div className="flex items-center gap-2 min-w-0">
                    <Store className="w-4 h-4 text-primary shrink-0" />
                    <span className="font-semibold text-sm truncate" data-testid={`cart-seller-name-${g.seller_id}`}>{g.name}</span>
                    <span className="text-xs text-muted-foreground">· {g.items.length} item{g.items.length !== 1 ? "s" : ""}</span>
                  </div>
                  <span className="text-sm font-semibold shrink-0">{format(g.subtotal)}</span>
                </div>
                <div className="flex items-center gap-1.5 px-4 py-2 text-xs text-green-700 dark:text-green-400 bg-green-50/60 dark:bg-green-950/20 border-b border-border/50" data-testid={`cart-seller-eta-${g.seller_id}`}>
                  <Truck className="w-3.5 h-3.5 shrink-0" />
                  <span>Est. delivery <strong>{deliveryEta(g)}</strong></span>
                </div>
                <div className="divide-y divide-border/50">
                  {g.items.map((i) => (
                    <div key={i.product_id} data-testid={`cart-row-${i.product_id}`} className="flex gap-4 p-4">
                      <img src={i.product?.images?.[0]} alt="" className="w-24 h-24 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <Link to={`/product/${i.product_id}`} className="font-medium hover:text-primary line-clamp-2">{i.product?.title}</Link>
                        <p className="text-sm text-muted-foreground">{i.product?.country}</p>
                        <p className="text-primary font-bold mt-1">{format(i.product?.discount_price || i.product?.price)}</p>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <button data-testid={`remove-${i.product_id}`} onClick={() => removeCart(i.product_id)}><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></button>
                        <div className="flex items-center border rounded-full">
                          <button data-testid={`dec-${i.product_id}`} onClick={() => updateCart(i.product_id, i.quantity - 1)} className="px-2.5 py-0.5">−</button>
                          <span className="px-2 text-sm font-semibold">{i.quantity}</span>
                          <button data-testid={`inc-${i.product_id}`} onClick={() => updateCart(i.product_id, i.quantity + 1)} className="px-2.5 py-0.5">+</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {groups.length > 1 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="cart-multi-seller-note">
                <Store className="w-3.5 h-3.5" />Items from {groups.length} sellers — one secure checkout covers them all.
              </p>
            )}
          </div>

          <div className="h-fit p-5 rounded-xl border border-border/60 bg-muted/20 space-y-4">
            <h2 className="font-display font-bold text-lg">{t("orderSummary")}</h2>
            <div className="flex gap-2">
              <Input data-testid="coupon-input" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder={t("couponCode")} />
              <Button data-testid="apply-coupon-btn" variant="outline" onClick={() => applyCoupon()}><Tag className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("subtotal")}</span><span>{format(subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-green-600"><span>{t("discount")}</span><span>−{format(discount)}</span></div>}
              <div className="flex justify-between"><span className="text-muted-foreground">{t("shipping")}</span><span>{shipping === 0 ? t("free") : format(shipping)}</span></div>
              {ngZone && (
                <div className="flex justify-between text-xs text-muted-foreground" data-testid="cart-nigeria-eta">
                  <span>Nigeria delivery · {ngZone.zone}</span><span>~{ngZone.eta_days} days</span>
                </div>
              )}
              {creditApplied > 0 && <div className="flex justify-between text-green-600" data-testid="cart-credit-line"><span>Store credit</span><span>−{format(creditApplied)}</span></div>}
              <div className="flex justify-between font-bold text-lg pt-2 border-t"><span>{t("total")}</span><span className="text-primary">{format(total)}</span></div>
            </div>

            {ngZones.length > 0 && (
              <div className="rounded-xl border border-border/60 p-3 space-y-1.5" data-testid="nigeria-delivery-box">
                <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Delivering within Nigeria?</label>
                <select
                  data-testid="nigeria-state-select"
                  value={ngState}
                  onChange={(e) => setNg(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select your state</option>
                  {ngZones.map((z) => (
                    <optgroup key={z.zone} label={`${z.zone} · ${format(z.fee)} · ~${z.eta_days} days`}>
                      {z.states.map((s) => <option key={s} value={s}>{s}</option>)}
                    </optgroup>
                  ))}
                </select>
                {ngZone && <p className="text-xs text-muted-foreground">Flat {format(ngZone.fee)} delivery to {ngState} ({ngZone.zone}), arriving in about {ngZone.eta_days} days.</p>}
                <p className="text-[11px] text-muted-foreground/80">This is an estimate — your final delivery fee is set by the address you choose at checkout.</p>
              </div>
            )}
            {credit > 0 && (
              <button type="button" onClick={() => setUseCredit((v) => !v)} data-testid="cart-use-credit-toggle"
                className={`w-full flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${useCredit ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/40"}`}>
                <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${useCredit ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}><Wallet className="w-4 h-4" /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold">{format(credit)} credit available</span>
                  <span className="block text-xs text-muted-foreground">{useCredit ? `Applying ${format(creditApplied)} to this order` : "Tap to apply it now"}</span>
                  {creditExpiry && (() => {
                    const days = Math.ceil((new Date(creditExpiry) - new Date()) / 86400000);
                    return days <= 30 && days > 0 ? <span className="block text-[11px] text-amber-600 font-medium mt-0.5" data-testid="cart-credit-expiry">Use it soon — expires in {days} day{days !== 1 ? "s" : ""}</span> : null;
                  })()}
                </span>
                <span className={`w-10 h-6 rounded-full relative transition-colors shrink-0 ${useCredit ? "bg-primary" : "bg-muted-foreground/30"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${useCredit ? "translate-x-4" : "translate-x-0.5"}`} />
                </span>
              </button>
            )}
            {hasAlcohol && (
              <div data-testid="age-verify-card" className="rounded-xl border p-4 space-y-2 bg-amber-50/60 dark:bg-amber-950/20 border-amber-300/60">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
                  <ShieldCheck className="w-4 h-4" /> Age verification required
                </div>
                {age?.status === "approved" ? (
                  <p className="flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400" data-testid="age-verify-approved">
                    <CheckCircle2 className="w-4 h-4" /> Your age is verified — you can buy alcohol.
                  </p>
                ) : age?.status === "pending" ? (
                  <p className="flex items-center gap-1.5 text-sm text-amber-700 dark:text-amber-300" data-testid="age-verify-pending">
                    <Clock className="w-4 h-4" /> ID submitted — under review. You'll be able to check out once approved.
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-muted-foreground">Your cart contains alcohol. Upload a photo of your government ID to confirm you're 18+. A staff member will approve it (one time only).</p>
                    {age?.status === "rejected" && (
                      <p className="text-xs text-destructive" data-testid="age-verify-rejected">Declined{age?.note ? `: ${age.note}` : ""}. Please upload a clearer ID.</p>
                    )}
                    <label className="inline-flex">
                      <input type="file" accept="image/*" className="hidden" onChange={uploadId} data-testid="age-verify-file" />
                      <Button asChild variant="outline" size="sm" className="rounded-full cursor-pointer" disabled={uploadingId}>
                        <span data-testid="age-verify-upload-btn">{uploadingId ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Upload ID</>}</span>
                      </Button>
                    </label>
                  </>
                )}
              </div>
            )}

            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Payment method</p>
              <Button data-testid="pay-paystack-btn" onClick={() => checkout("paystack")} disabled={loading || !ageOk} size="lg"
                className="w-full rounded-full justify-between px-5">
                <span className="flex items-center gap-2"><Banknote className="w-4 h-4" /> Pay with Paystack (₦)</span>
                <span className="font-bold">₦{ngnTotal.toLocaleString()}</span>
              </Button>
              {loading && <div className="flex justify-center pt-1"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
            </div>
            <p className="text-xs text-muted-foreground text-center">Secure checkout · Paid securely via Paystack (₦)</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
